import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import InputWithCharacterCounter from '../../../slides/highlighted/common/InputWithCharacterCounter';
import {formatForInputDate, getFutureDate} from '../../../../utils/dateTimeUtils';
import {ProtectionInfo, ProtectionType} from '../../../../incase-domain/presentationProtection';
import ProtectionOptionsFormWrapper from './ProtectionOptionsFormWrapper';
import {randomString} from '../../../../utils/utils';
import TooltipWrapper from '../../../../ui/components/common/TooltipWrapper';

interface PasswordFormProps {
    protection: ProtectionInfo;
    setProtection: (protection: ProtectionInfo) => void;
    errors: {
        password?: {message: string};
        validUntil?: {message: string};
    };
}

function PasswordForm({protection, setProtection, errors}: PasswordFormProps): JSX.Element {
    const {t} = useTranslation();
    const [passwordError, setPasswordError] = useState<string>('');
    const passwordProtected = protection.types.includes(ProtectionType.PASSWORD);

    const setPassword = (password: string): void => {
        setProtection({
            ...protection,
            password,
        });
        if (!password) {
            setPasswordError(t('presEditPage.settings.presProtection.validationErrors.password'));
        } else {
            setPasswordError('');
        }
    };

    const setValidUntil = (validUntil: string): void => {
        setProtection({
            ...protection,
            validUntil,
        });
    };

    const handleAddPassword = (): void => {
        setProtection({
            ...protection,
            types: protection.types.filter((t) => t !== ProtectionType.GUEST).concat(ProtectionType.PASSWORD),
            password: 'nobula42',
            validUntil: getFutureDate(14).toDateString(),
        });
    };

    const handleRemovePassword = (): void => {
        // if both IDENTIFICATION and PASSWORD are removed, add GUEST
        const noIdentificationType = !protection.types.includes(ProtectionType.IDENTIFICATION);
        setProtection({
            ...protection,
            types: protection.types.filter((t) => t !== ProtectionType.PASSWORD).concat(noIdentificationType ? ProtectionType.GUEST : []),
            password: '',
            validUntil: '',
        });
    };

    return (
        <ProtectionOptionsFormWrapper
            label={t('presEditPage.settings.presProtection.password')}
            checked={passwordProtected}
            onToggle={passwordProtected ? handleRemovePassword : handleAddPassword}
        >
            <TooltipWrapper id={randomString(10)} content={t('presEditPage.settings.presProtection.password')} delayShow={500}>
                <div>
                    <InputWithCharacterCounter
                        className="c-sidepanel__input"
                        placeholder={t('presEditPage.settings.presProtection.inputPlaceholder')}
                        value={protection.password || ''}
                        onChange={(value) => setPassword(value)}
                        data-cy="protection_password_input"
                        error={passwordError}
                    />
                </div>
            </TooltipWrapper>

            <TooltipWrapper id={randomString(10)} content={t('presEditPage.settings.presProtection.validUntil')} delayShow={500}>
                <div className="c-sidepanel__input">
                    <input
                        type="date"
                        id="start"
                        name="trip-start"
                        value={formatForInputDate(protection.validUntil || getFutureDate(14))}
                        onChange={(e) => setValidUntil(e.target.value)}
                        min={formatForInputDate(getFutureDate(0))}
                    />
                </div>
                {errors.validUntil && (
                    <div className="error-msg u-flex u-h-right">
                        {/* <p style={{color: 'red'}}>{t(`${errors.validUntil?.message}`)}</p> */}
                    </div>
                )}
            </TooltipWrapper>
        </ProtectionOptionsFormWrapper>
    );
}

export default PasswordForm;
